
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageDetectionService } from '../services/languageDetection';

interface LanguageContextType {
  currentLanguage: string;
  changeLanguage: (language: string) => Promise<void>;
  availableLanguages: any[];
  isDetecting: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'pt-BR');
  const [isDetecting, setIsDetecting] = useState(false);

  const availableLanguages = LanguageDetectionService.getAvailableLanguages();

  useEffect(() => {
    const detectAndSetLanguage = async () => {
      setIsDetecting(true);
      try {
        const detectedLanguage = await LanguageDetectionService.detectLanguage();
        if (detectedLanguage !== currentLanguage) {
          await i18n.changeLanguage(detectedLanguage);
          setCurrentLanguage(detectedLanguage);
          
          // Aplicar direção RTL se necessário
          const selectedLang = availableLanguages.find(lang => lang.code === detectedLanguage);
          if (selectedLang?.rtl) {
            document.documentElement.dir = 'rtl';
          } else {
            document.documentElement.dir = 'ltr';
          }
          document.documentElement.lang = detectedLanguage;
        }
      } catch (error) {
        console.error('Language detection failed:', error);
      } finally {
        setIsDetecting(false);
      }
    };

    detectAndSetLanguage();
  }, []);

  const changeLanguage = async (languageCode: string) => {
    try {
      await i18n.changeLanguage(languageCode);
      setCurrentLanguage(languageCode);
      LanguageDetectionService.saveLanguagePreference(languageCode);
      
      // Aplicar direção RTL se necessário
      const selectedLang = availableLanguages.find(lang => lang.code === languageCode);
      if (selectedLang?.rtl) {
        document.documentElement.dir = 'rtl';
      } else {
        document.documentElement.dir = 'ltr';
      }
      document.documentElement.lang = languageCode;
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  const value = {
    currentLanguage,
    changeLanguage,
    availableLanguages,
    isDetecting
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguageContext = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  return context;
};
